"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import {
	createContext,
	Suspense,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	FIRST_TOUCH_KEYS,
	type FirstTouchKey,
} from "@/lib/analytics/acquisition-touch";
import { classifyAiReferrer } from "@/lib/analytics/ai-referrer";
import { isSelfReferral } from "@/lib/analytics/channel";
import { deriveFunnelStage } from "@/lib/analytics/funnel-stage";
import { isHeatmapRoute } from "@/lib/analytics/heatmap-routes";
import { posthog } from "@/lib/analytics/posthog";
import {
	postHogBeforeSend,
	sanitizeUrl,
} from "@/lib/analytics/posthog-before-send";
import {
	daysSinceLast,
	shouldEmitReturnVisit,
} from "@/lib/analytics/return-visit";
import { shouldEnableSupportWidget } from "@/lib/analytics/support-widget";
import { useSession } from "@/lib/auth/client";
import { useHasConsent } from "@/lib/consent/hooks";
import { useLegitimateInterest } from "@/lib/consent/region-context";
import { contentCampaign } from "@/lib/content/attribution-url";
import { recordContentClick } from "@/lib/content/click-beacon";
import { apexCookieDomain } from "@/lib/cookies/apex-domain";
import { setClientCookie } from "@/lib/cookies/client";
import { recordSocialClick } from "@/lib/social/click-beacon";
import { AttributionCapture } from "./attribution-capture";

const PostHogReadyContext = createContext(false);

/**
 * Returns true once PostHog has been initialised and capture is enabled for this
 * session. Product analytics rides "necessary" consent (legitimate interest), so
 * this is true for every visitor OUTSIDE the strict-consent regions (EU/EEA/UK/
 * CH); inside them it waits on an explicit "analytics" opt-in. Components use it
 * to know the client is ready before firing a `capture`.
 */
export function usePostHogReady(): boolean {
	return useContext(PostHogReadyContext);
}

const ATTRIBUTION_KEYS = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
	"gclid",
	"fbclid",
	"ref",
] as const;

type AttributionPayload = Partial<
	Record<(typeof ATTRIBUTION_KEYS)[number], string>
> & { referrer_host?: string; ai_referrer?: string };

export function readAttribution(
	searchParams: URLSearchParams,
): AttributionPayload {
	const out: AttributionPayload = {};
	for (const key of ATTRIBUTION_KEYS) {
		const value = searchParams.get(key);
		if (value && value.length > 0 && value.length <= 200) {
			out[key] = value;
		}
	}
	if (typeof document !== "undefined" && document.referrer) {
		try {
			const refUrl = new URL(document.referrer);
			// The `origin` check alone only catches an exact same-host referrer —
			// it does NOT catch the apex→`app.lokerdollar.com` hop (different
			// origins, same site), which is exactly the self-referral shape plan
			// P0.3(a) measured: 16 lifetime users record `app.lokerdollar.com` as
			// their own acquisition source. `isSelfReferral` closes that gap.
			if (
				refUrl.origin !== window.location.origin &&
				!isSelfReferral(refUrl.hostname)
			) {
				out.referrer_host = refUrl.hostname;
				// Tag AI-search referrers (ChatGPT, Perplexity, Gemini, Claude,
				// Copilot, …) as a first-class channel — GEO backlog P4. Only set
				// when the host classifies as AI so non-AI traffic stays unaffected.
				const aiSource = classifyAiReferrer(refUrl.hostname);
				if (aiSource) out.ai_referrer = aiSource;
			}
		} catch {
			// ignore — malformed referrer
		}
	}
	return out;
}

function applyAttribution(
	posthogClient: typeof posthog,
	attribution: AttributionPayload,
) {
	if (Object.keys(attribution).length === 0) return;
	// Last-touch as a regular set; first-touch as $set_once via setPersonProperties.
	const initial: Record<string, string> = {};
	for (const [k, v] of Object.entries(attribution)) {
		if (v) initial[`initial_${k}`] = v;
	}
	posthogClient.setPersonProperties?.(attribution, initial);
	// Make the last-touch values available on every subsequent event.
	posthogClient.register?.(attribution);
}

/**
 * First-touch acquisition cookie. The server-side `user_signup_completed` event
 * fires via a direct PostHog HTTP POST that cannot see the browser's
 * `register()`/person-property state, so without this the signup is unattributed
 * (every signup landed as utm_source = NULL even when arriving from a tagged
 * Threads/AI-search link). We persist the first *attributed* touch to a plain
 * cookie that the auth hook reads (same mechanism as `referral_code`). Necessary
 * consent: stores only campaign metadata, no identifier/PII.
 */
const FIRST_TOUCH_COOKIE = "ld_attr";

/**
 * localStorage key holding the epoch-ms timestamp of the visitor's last
 * recorded pageview, used to detect a "return visit" (>24h gap) — see
 * `shouldEmitReturnVisit` in `@/lib/analytics/return-visit`. localStorage
 * (not a cookie) because this is pure client-side gap math with no server
 * dependency, and — unlike `ld_attr` — updates on every page, so a cookie
 * would re-send a Set-Cookie-sized header on every navigation for no reason.
 */
const LAST_SEEN_KEY = "ld_last_seen_ts";

/**
 * Writes the `ld_attr` first-touch cookie. Takes the widened
 * `FIRST_TOUCH_KEYS` key set (`@/lib/analytics/acquisition-touch`) — the
 * single source of truth this cookie's writer (here) and its server-side
 * reader (`src/lib/analytics/first-touch.ts`) both import, so the two ends
 * can no longer diverge. Callers may pass any subset; a key absent from the
 * input is simply omitted from the cookie, never written as null.
 */
export function persistFirstTouchAttribution(
	attribution: Partial<Record<FirstTouchKey, string>>,
) {
	if (typeof document === "undefined") return;
	// First-touch: never overwrite an existing value.
	if (document.cookie.includes(`${FIRST_TOUCH_COOKIE}=`)) return;
	const payload: Partial<Record<FirstTouchKey, string>> = {};
	for (const k of FIRST_TOUCH_KEYS) {
		const v = attribution[k];
		if (v) payload[k] = v;
	}
	// Skip direct/untagged first visits so the cookie captures the first touch
	// that actually carries acquisition signal.
	if (Object.keys(payload).length === 0) return;
	const value = JSON.stringify(payload);
	setClientCookie(FIRST_TOUCH_COOKIE, value, {
		maxAgeSeconds: 60 * 60 * 24 * 90, // 90 days
		domain: apexCookieDomain(),
	});
}

function PostHogPageView() {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	useEffect(() => {
		if (!pathname) return;
		const attribution = readAttribution(
			new URLSearchParams(searchParams.toString()),
		);

		// First-party content (blog) attribution: record the read into
		// `content_attribution` (necessary consent, no PII), and — when the reader
		// arrived organically with no campaign of its own — synthesize a
		// `content` first touch keyed on the post slug, so a later signup/apply
		// attributes back to the post. A real utm_campaign (e.g. a social share of
		// the post) is left untouched.
		const contentLanding = recordContentClick({
			pathname,
			utmMedium: attribution.utm_medium,
			hasReferrer: Boolean(attribution.referrer_host),
		});
		if (contentLanding && !attribution.utm_campaign) {
			attribution.utm_source = attribution.utm_source ?? "content";
			attribution.utm_medium = attribution.utm_medium ?? contentLanding.medium;
			attribution.utm_campaign = contentCampaign(contentLanding.slug);
		}

		// Persist first-touch acquisition for the server-side signup event to read.
		persistFirstTouchAttribution(attribution);

		// First-party social-click measurement rides "necessary" consent, NOT
		// opt-in "analytics". It records { campaign, platform } plus the
		// pseudonymous PostHog distinct id this same pageview already carries, so
		// the click row can join the later signup (no cross-site cookie, no PII).
		let anonId: string | null = null;
		try {
			anonId = posthog?.get_distinct_id?.() ?? null;
		} catch {
			// PostHog not ready — record the click without the join key.
		}
		recordSocialClick({
			source: attribution.utm_source,
			campaign: attribution.utm_campaign,
			medium: attribution.utm_medium,
			anonId,
		});

		// `$pageview` + first-touch attribution are product analytics on the
		// "necessary" basis — fire for every visitor. Setting the first-touch
		// `initial_*` person properties here (for all users) is what makes the
		// user-keyed server `checkout_completed` / `referral_attributed` events
		// attributable to their acquisition channel once the user is identified.
		if (!posthog) return;
		let url = window.location.origin + pathname;
		if (searchParams.toString()) {
			url = `${url}?${searchParams.toString()}`;
		}
		applyAttribution(posthog, attribution);
		// Content-funnel stage (F14): tof/mof/bof breakdown for the funnel-matrix
		// insight (TOF entry → MOF view → BOF view → signup). Omitted entirely on
		// a core-product route (homepage, jobs, auth, admin, …) — see
		// src/lib/analytics/funnel-stage.ts.
		const funnelStage = deriveFunnelStage(pathname);
		posthog.capture("$pageview", {
			$current_url: sanitizeUrl(url),
			...attribution,
			...(funnelStage ? { funnel_stage: funnelStage } : {}),
		});
	}, [pathname, searchParams]);

	return null;
}

function PostHogAuth() {
	const { data: session } = useSession();
	const lastIdentifiedUserId = useRef<string | null>(null);
	const analyticsAllowed = useHasConsent("analytics");

	useEffect(() => {
		if (!posthog) return;

		const userId = session?.user?.id ?? null;

		if (userId) {
			// Identify with the pseudonymous user id for EVERY logged-in visitor —
			// this is the join key that stitches anonymous client events to the
			// user-keyed server events (checkout, referral) into one funnel, and is
			// itself first-party (no PII), so it rides "necessary" consent. The
			// actual PII — email + name — is attached only when the user grants
			// "analytics" consent. `user_state` (pro/free) is non-PII product state.
			const personProps: Record<string, unknown> = {
				user_state:
					(session?.user as { role?: string } | undefined)?.role === "pro"
						? "pro"
						: "free",
			};
			if (analyticsAllowed) {
				personProps.email = session?.user?.email;
				personProps.name = session?.user?.name;
			}
			posthog.identify(userId, personProps);
			lastIdentifiedUserId.current = userId;
			return;
		}

		// Logged out: only reset when we previously had an identified user, so we
		// don't churn the anonymous distinct_id on first load before auth resolves.
		if (lastIdentifiedUserId.current !== null) {
			posthog.reset();
			lastIdentifiedUserId.current = null;
		}
	}, [session, analyticsAllowed]);

	return null;
}

let posthogInitialized = false;
// TTFB is a per-page-load metric (Navigation Timing), so capture it once per
// document, not on every SPA route change.
let ttfbCaptured = false;

/**
 * Split navigation TTFB into its phases (ms, rounded). `fetch_start_ms` covers
 * everything before the fetch: cross-origin redirects (Google, email click
 * trackers, shorteners), unload and service worker; `redirect_ms` is the
 * same-origin redirect share only (cross-origin redirects zero it). A reused
 * connection reports 0 for dns/connect, which is a real value, not missing.
 */
export function navTimingBreakdown(
	nav: PerformanceNavigationTiming,
): Record<string, number | string> {
	const ms = (v: number) => Math.max(0, Math.round(v));
	const out: Record<string, number | string> = {
		fetch_start_ms: ms(nav.fetchStart),
		redirect_ms: ms(nav.redirectEnd - nav.redirectStart),
		redirect_count: nav.redirectCount,
		dns_ms: ms(nav.domainLookupEnd - nav.domainLookupStart),
		connect_ms: ms(nav.connectEnd - nav.connectStart),
		request_wait_ms: ms(nav.responseStart - nav.requestStart),
	};
	if (nav.secureConnectionStart > 0) {
		out.tls_ms = ms(nav.connectEnd - nav.secureConnectionStart);
	}
	if (nav.nextHopProtocol) out.protocol = nav.nextHopProtocol;
	return out;
}

// Read Time-To-First-Byte from the Navigation Timing entry and send it to
// PostHog. posthog-js's web-vitals autocapture only wires LCP/CLS/FCP/INP (no
// onTTFB handler + "TTFB" isn't even in its SupportedWebVitalsMetrics type), so
// `$web_vitals` never carries TTFB. TTFB = server response time — the one vital
// that surfaces Cloudflare Worker cold-cache / force-dynamic latency, the number
// we most need for a mid-range-device Indonesian audience. Reading `responseStart`
// off the existing PerformanceNavigationTiming entry is a passive lookup (no new
// dependency, no observer, no INP cost like heatmaps) and rides a small custom
// event; PostHog auto-attaches $pathname/$geoip so it slices by route + country.
function captureTtfb(): void {
	if (ttfbCaptured) return;
	if (typeof window === "undefined" || typeof performance === "undefined")
		return;
	const nav = performance.getEntriesByType?.("navigation")?.[0] as
		| PerformanceNavigationTiming
		| undefined;
	if (!nav) return;
	// web-vitals defines TTFB as responseStart clamped to >= 0; the entry's times
	// are already relative to its own startTime, so responseStart IS the TTFB.
	const ttfb = Math.max(0, Math.round(nav.responseStart));
	if (!Number.isFinite(ttfb) || ttfb <= 0) return;
	ttfbCaptured = true;
	// Server-Timing entries appended by src/worker.ts withServerTiming():
	// `worker;dur` = server's share of TTFB (headers-ready time inside the
	// Worker); `nextcache`/`edgecache` = the ISR / colo-cache verdicts. These
	// decompose field TTFB into server vs network — the response headers
	// themselves (x-nextjs-cache, cf-cache-status) are not readable from page
	// JS, but same-origin Server-Timing is. Entries are absent on zone workers
	// (default OpenNext entry) — properties are simply omitted there.
	const serverTiming = (nav.serverTiming ??
		[]) as readonly PerformanceServerTiming[];
	const workerEntry = serverTiming.find((e) => e.name === "worker");
	const mwEntry = serverTiming.find((e) => e.name === "mw");
	const mwRlEntry = serverTiming.find((e) => e.name === "mwrl");
	const coldEntry = serverTiming.find((e) => e.name === "cold");
	const nextCacheEntry = serverTiming.find((e) => e.name === "nextcache");
	const edgeCacheEntry = serverTiming.find((e) => e.name === "edgecache");
	// colo;desc = the data center the request ENTERED (request.cf.colo), NOT
	// where the isolate ran — see the note on `withServerTiming` in worker.ts.
	const coloEntry = serverTiming.find((e) => e.name === "colo");
	try {
		posthog.capture?.("web_vitals_ttfb", {
			ttfb_ms: ttfb,
			navigation_type: nav.type,
			...(workerEntry
				? { server_dur_ms: Math.round(workerEntry.duration) }
				: {}),
			// mw;dur = the middleware tax (bot-classify, KV, rate-limit, CSP);
			// server_dur_ms - mw_dur_ms ≈ the actual page render.
			...(mwEntry ? { mw_dur_ms: Math.round(mwEntry.duration) } : {}),
			// mwrl;dur = the RateLimit-binding round-trip inside the middleware tax.
			// Big mwrl share ⇒ the rate-limit DO call is the lever, not KV reads.
			...(mwRlEntry ? { mw_ratelimit_ms: Math.round(mwRlEntry.duration) } : {}),
			// cold;desc = "1" when this was the isolate's first request. Lets us
			// attribute the worker;dur→TTFB gap to cold-start/placement vs network.
			...(coldEntry?.description
				? { cold_start: coldEntry.description === "1" }
				: {}),
			...(nextCacheEntry?.description
				? { next_cache: nextCacheEntry.description }
				: {}),
			...(edgeCacheEntry?.description
				? { edge_cache: edgeCacheEntry.description }
				: {}),
			// The entry data center — a finer-grained geo than country, which is
			// what makes "who pays the long hop" legible once execution is pinned
			// to one place (2026-09-06: apex is `placement.targeted` on APAC).
			...(coloEntry?.description ? { colo: coloEntry.description } : {}),
			// Where the time before the Worker goes. Field 2026-10-29: cached pages
			// (server_dur ~20ms) still had TTFB p75 ~2s outside Indonesia, and the
			// event could not say whether that was redirects, connection setup or
			// the request wait. All read off the same navigation entry.
			...navTimingBreakdown(nav),
		});
	} catch {
		// posthog may not be fully ready in tests
	}
}

/**
 * `capture_performance` for posthog.init. Only `web_vitals_attribution` is set:
 * leaving `web_vitals` and `network_timing` undefined keeps the project's
 * server-side remote config authoritative for both (posthog-js falls back to
 * it for any key that is not a boolean here).
 *
 * Why attribution: `/id/jobs` field CLS p75 was 0.26 (CrUX, 2026-10-07), and
 * every `$web_vitals` CLS event arrived with `entries: [{}]` — no element, so
 * the shift could only be guessed from code. Measured 2026-10-12 over the
 * 14 days before (non-bot, /id/jobs + /en/jobs): URLs carrying `#feed` (the
 * search / pagination `router.push` target) had CLS p75 0.299 with 52 of 83
 * events over 0.1, against p75 0.018 (7 of 93) without it. The attribution
 * build adds `largestShiftTarget` / `largestShiftSource` to each CLS event, so
 * the next fix targets the element that actually moved. The attribution
 * script is lazy-loaded by posthog-js from its CDN on the client; it is not
 * part of the Worker bundle.
 */
export const POSTHOG_CAPTURE_PERFORMANCE = {
	web_vitals_attribution: true,
} as const;

/**
 * Turn PostHog heatmap capture on for `isHeatmapRoute` pages and off
 * elsewhere. Only calls `set_config` when the value changes, so ordinary
 * feed navigation costs nothing.
 */
export function syncHeatmapCapture(
	pathname: string | null | undefined,
	client: Pick<typeof posthog, "set_config" | "config"> = posthog,
): void {
	const want = isHeatmapRoute(pathname);
	try {
		const current = client.config?.capture_heatmaps;
		const isOn = current !== false && current !== undefined;
		if (isOn === want) return;
		client.set_config({ capture_heatmaps: want });
	} catch {
		// Telemetry-only; never break navigation for it.
	}
}

function ensurePostHogInitialized() {
	if (posthogInitialized) return;
	if (typeof window === "undefined") return;
	const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
	const host =
		process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
	const isLocalhost =
		window.location.hostname === "localhost" ||
		window.location.hostname === "127.0.0.1";

	if (!key || isLocalhost) {
		posthogInitialized = true;
		return;
	}

	posthog.init(key, {
		api_host: host,
		person_profiles: "always",
		capture_pageview: false,
		// posthog-js defaults this to `'if_capture_pageview'`, which resolves to
		// OFF here because we capture pageviews manually (PostHogPageView) and
		// therefore set `capture_pageview: false`. The consequence was silent and
		// total: ZERO `$pageleave` events in the 30 days to 2026-08-26, across
		// every device class. Without a pageleave, PostHog cannot close out the
		// final pageview of a session, so session duration and bounce rate are
		// both computed from incomplete data — which is precisely the pair of
		// metrics that read as a 133% engagement collapse on 2026-08-11 while
		// mobile pageviews-per-session never moved (3.17 -> 3.61).
		// Explicit `true` decouples pageleave from the manual-pageview choice.
		capture_pageleave: true,
		before_send: postHogBeforeSend,
		// Product analytics (pageviews, autocapture, funnel/tool/share events) is
		// first-party, single-domain, no-PII measurement and rides "necessary"
		// consent — the same legitimate-interest basis the social-click beacon
		// already uses (see PostHogPageView). So once we initialise, capturing is
		// on; we do NOT opt out by default. WHETHER we initialise is the gate:
		// PostHogProvider only calls this for visitors who have consented OR are
		// outside the strict-consent regions (EU/EEA/UK/CH) — see `captureEnabled`.
		// The two privacy-sensitive surfaces stay behind explicit "analytics"
		// consent in every region and are gated separately: session replay (started
		// only on consent — see PostHogProvider) and PII identification (email/name
		// attached only on consent — see PostHogAuth). Marketing pixels ride their
		// own "marketing" consent in marketing-scripts.tsx.
		opt_out_capturing_by_default: false,
		// Durable storage so distinct_id survives across page loads — a stable id
		// is what lets anonymous client events reconcile with the user-keyed server
		// events (checkout, referral) into a single funnel.
		persistence: "localStorage+cookie",
		// The site is two Cloudflare Workers under one domain — marketing on the
		// apex, Hunt/app on `app.lokerdollar.com` — and the hero CTA sends people
		// straight across that boundary (`/seeker?src=home_hero`). posthog-js
		// writes a HOST-ONLY cookie by default and `localStorage` is per-origin,
		// so without this flag the same visitor is re-anonymised on the single
		// most important hop in the funnel: measured 2026-08-27, only 261 people
		// in 30 days were ever seen on two hosts, and the sessions that DID span
		// both were the best on the site (0% bounce, 256s median, 5.7 pageviews).
		// `src/lib/consent/storage.ts` already scopes ITS cookie to
		// `.lokerdollar.com` for exactly this reason; this brings analytics
		// identity in line with consent identity.
		cross_subdomain_cookie: true,
		autocapture: true,
		// `autocapture` only covers clicks/pageviews/DOM events — it does NOT wire
		// exception parsing. Without this, posthog-js's baseline window.onerror
		// safety net still emits an `$exception` event but skips the stack-parsing
		// pipeline, so `$exception_type`/`$exception_message`/`$exception_list` all
		// ship null (87 such events across /jobs, /seeker, /settings in the 21 days
		// before this fix — ungroupable, untriageable). This turns on posthog-js's
		// full exception-autocapture extension (window.onerror +
		// unhandledrejection listeners with proper stack parsing) so every future
		// client `$exception` carries a type + message. Independent of the
		// server-side path (`captureServerException` in
		// src/lib/analytics/server-error-events.ts, wired via `onRequestError` in
		// src/instrumentation.ts) — that one already populates these fields.
		capture_exceptions: true,
		capture_performance: POSTHOG_CAPTURE_PERFORMANCE,
		// Heatmaps add a second pointer/scroll observer on top of autocapture. On
		// mid-range Android (the dominant device class for our Indonesian users)
		// the extra main-thread work regresses INP by 50–150 ms per interaction
		// for a signal we rarely act on — keep this off site-wide. The low-traffic
		// decision pages in `isHeatmapRoute` (/pricing, /employers, /tools) are
		// the exception; `PostHogHeatmapRoutes` flips it per route on navigation.
		capture_heatmaps: isHeatmapRoute(window.location.pathname),
		// Dead-click capture shares that same INP-cost class, and was off for the
		// same reason — but it had produced 0 `$dead_click` events/30d despite the
		// schema existing (2026-08-10 UX audit), i.e. we were paying zero signal
		// for the "off" default anyway. Turning it on to get the signal; if field
		// INP on /id/jobs (mobile p75, currently 232ms) regresses after this
		// ships, this is the first thing to revert.
		capture_dead_clicks: true,
		// Surveys stay OFF permanently — PostHog-native survey broadcasts (CSAT,
		// hunt turn feedback) are a dead explicit-feedback instrument (60d: CSAT
		// 51 shown / 0 responses, hunt_turn_feedback 1 event) removed 2026-08-12
		// (P2-12, telemetry-discovery-pmf-audit-2026-08-12). Never re-enable this
		// without re-adding the idle-callback opt-in effect this PR removed —
		// loading surveys.js inside the LCP window regressed hydration on
		// mid-range Android (our dominant device class), which is the original
		// reason this stayed off at init even while surveys were briefly live.
		disable_surveys: true,
		// PostHog Support (conversations) — the B2B chat frontline. Loaded ONLY
		// on the surfaces `shouldEnableSupportWidget` names, for exactly the
		// reason `disable_surveys` above is permanent: the widget arrives as an
		// extra external script fetched on the remote-config response, the same
		// shape as surveys.js, whose load inside the LCP window regressed
		// hydration on mid-range Android. The apex feed and job pages are the
		// highest-traffic, most LCP-sensitive, most crawler-dominated surface on
		// the site and get zero support value from it.
		//
		// This is read at INIT ONLY — posthog-js's `conversations` extension bails
		// out of both `onRemoteConfig()` and `loadIfEnabled()` on this flag, and
		// `show()` never force-loads (it warns "Conversations not loaded yet."),
		// so a later `set_config({ disable_conversations: false })` cannot bring
		// the widget back. Do not try to build a click-to-load launcher on top of
		// it; `SupportLauncher` reveals-or-falls-back instead.
		//
		// The rollout/kill switch lives in PostHog (Support → Settings → In-app
		// widget), which gates the remote config for every page at once. Never
		// add a repo-side constant that shadows it.
		disable_conversations: !shouldEnableSupportWidget({
			hostname: window.location.hostname,
			pathname: window.location.pathname,
		}),
		// Session recording captures replays — privacy-sensitive, so it stays
		// OFF at init and is started only once `captureEnabled` is true (see
		// PostHogProvider's replay effect). Mask all inputs by default and
		// require an explicit opt-out via a `data-private` selector to be unmasked
		// — we err on the side of privacy so password fields, email inputs, and
		// resume content are never recorded.
		//
		// 2026-08-04: the replay effect used to gate on `analyticsAllowed` alone —
		// STRICTER than event capture (`captureEnabled` = analyticsAllowed ||
		// legitimateInterestOk === true), even though replay is first-party,
		// no-PII (masked inputs), single-domain measurement — the same legal
		// basis product analytics already runs on outside the EEA/UK/Switzerland
		// prior-consent zone. That mismatch meant Replay Vision had no input for
		// the ~85%+ of visitors who never see an explicit consent prompt:
		// $recording_status was active on 1 event in 7 days, ~102 people/7d. The
		// 2026-07-23 sampleRate 0.1→0.2 bump was a no-op against that — sampling
		// was never the binding constraint, the gate was. Fix: replay now uses
		// the identical `captureEnabled` expression as capture (same effect,
		// same dependency), and sampleRate is raised to reflect that replay is no
		// longer artificially starved. EEA/UK/Switzerland visitors are unaffected
		// — they only reach `captureEnabled` via explicit "analytics" opt-in,
		// same as before (see `useLegitimateInterest` / `requiresPriorConsent`).
		// Content masking (below) is unchanged — this is a legal-basis change,
		// never a privacy-of-recorded-content change.
		//
		// sampleRate 1.0 on both hosts: Hunt (app.lokerdollar.com) and the
		// marketing site share this one PostHog project + provider. Hunt was
		// previously raised to 0.5 (marketing stayed at 0.2) because Hunt's low
		// session volume meant a flat lower rate barely captured any Hunt chatbot
		// sessions. Both now sample every session — with `captureEnabled` fixing
		// the real constraint (who gets recorded at all), a shared 100% keeps the
		// two hosts' coverage comparable instead of reintroducing a rate gap.
		disable_session_recording: true,
		session_recording: {
			sampleRate: 1.0,
			maskAllInputs: true,
			maskTextSelector: "[data-private]",
			maskInputFn: (text, element) => {
				const type = element?.getAttribute?.("type");
				if (type === "password" || type === "email")
					return "*".repeat(text.length);
				const name = element?.getAttribute?.("name") ?? "";
				if (/email|password|token|otp|resume/i.test(name)) {
					return "*".repeat(text.length);
				}
				return text;
			},
		},
	});
	posthogInitialized = true;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
	const analyticsAllowed = useHasConsent("analytics");
	// Return-visit detection needs to re-check on every navigation ("runs on
	// every page"), not just once per session — see the effect below.
	const pathname = usePathname();
	// Geo gate: product analytics rides "necessary" consent (legitimate interest)
	// for most of the world, but the EU/EEA, UK, and Switzerland require prior
	// opt-in. Resolved once for the whole app by `<ConsentRegionProvider>`
	// (see `@/lib/consent/region-context`) from `/api/privacy-region`, so the
	// page HTML stays geo-free/edge-cacheable and no other consumer re-fetches
	// the same region. `null` = not yet resolved.
	const legitimateInterestOk = useLegitimateInterest();
	const [ready, setReady] = useState(false);

	useEffect(() => {
		const handler = (e: ErrorEvent) => {
			if (e.message === "Script error." && !e.filename) {
				e.stopImmediatePropagation();
				e.preventDefault();
			}
		};
		window.addEventListener("error", handler, true);
		return () => window.removeEventListener("error", handler, true);
	}, []);

	// Capture is enabled when the user has explicitly consented (any region) OR
	// when they're in a legitimate-interest region. EU/EEA/UK/CH visitors without
	// consent never reach this branch, so the PostHog client is never initialised
	// and no cookie is written for them until they opt in.
	const captureEnabled = analyticsAllowed || legitimateInterestOk === true;

	useEffect(() => {
		if (captureEnabled) {
			ensurePostHogInitialized();
			try {
				posthog.opt_in_capturing?.();
			} catch {
				// noop
			}
			setReady(true);
			captureTtfb();
		} else if (posthogInitialized) {
			// A previously-consented EU user who withdrew consent: stop capturing.
			try {
				posthog.opt_out_capturing?.();
			} catch {
				// noop
			}
		}
	}, [captureEnabled]);

	// Return-visit signal (pre-B2B gate PR-5, B4): W1 return rate was measured
	// at ~3.8% with ZERO instrumentation of the return-visit stage itself. Reads
	// `ld_last_seen_ts` from localStorage on every page; if the gap exceeds 24h
	// (pure math in `shouldEmitReturnVisit`), fires `return_visit` with
	// `{ days_since_last }`, then ALWAYS refreshes the stored timestamp to now —
	// regardless of whether this pageview fired the event — so the next check
	// measures the gap from this visit, not from the last return-visit fire.
	// Gated on `captureEnabled` (same as the other consent-gated effects here):
	// never read/write localStorage or capture for a visitor we aren't
	// capturing for.
	//
	// P2-2 (product-signal-audit-remediation 2026-08-04 #13): the 2026-08-04
	// audit found `return_visit` firing for only 8 people in 30 days against
	// ~2,800 visitors — this `captureEnabled` gate structurally excludes the
	// returning-but-unconsented population the metric exists to measure (a
	// visitor who never opted in never gets a `return_visit`, no matter how
	// many times they come back). P0-2 (a separate, in-flight PR) changes
	// `captureEnabled`'s definition as a side effect and should widen this
	// population — do NOT duplicate that change here. Re-measure `return_visit`
	// volume against live PostHog data AFTER P0-2 merges (this PR has no prod
	// data access to verify it) — if it stays near zero, either fix this gate
	// directly or rename the event so nobody reads 8/30d as the real return
	// rate.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname is intentionally the re-run trigger ("runs on every page"); its value itself is unused inside the effect.
	useEffect(() => {
		if (!captureEnabled || !posthogInitialized || !posthog) return;
		if (typeof window === "undefined") return;
		try {
			const raw = window.localStorage.getItem(LAST_SEEN_KEY);
			const lastSeenMs = raw ? Number(raw) : null;
			const nowMs = Date.now();
			if (lastSeenMs !== null && shouldEmitReturnVisit(lastSeenMs, nowMs)) {
				posthog.capture?.("return_visit", {
					days_since_last: daysSinceLast(lastSeenMs, nowMs),
				});
			}
			window.localStorage.setItem(LAST_SEEN_KEY, String(nowMs));
		} catch {
			// localStorage may be unavailable (private browsing, disabled storage,
			// SSR) — never break the app for this telemetry-only signal.
		}
	}, [captureEnabled, pathname]);

	// Heatmaps follow the route on client-side navigation: init reads the
	// landing path, this keeps it in step when a visitor moves between the
	// feed (off) and a decision page (on). posthog-js `set_config` re-runs
	// `heatmaps.startIfEnabled()`, which starts or stops the observer.
	useEffect(() => {
		if (!captureEnabled || !posthogInitialized || !posthog) return;
		syncHeatmapCapture(pathname);
	}, [captureEnabled, pathname]);

	// Session replay is privacy-sensitive, so it starts only once `captureEnabled`
	// is true, and stops if that ever flips back off. Init keeps replay off
	// (disable_session_recording: true), so this is the sole enable path.
	//
	// 2026-08-04: this used to gate on `analyticsAllowed` alone — the explicit
	// "analytics" opt-in — which is STRICTER than event capture's
	// `captureEnabled` (analyticsAllowed || legitimateInterestOk === true).
	// Replay is first-party, no-PII (all inputs masked — see
	// `ensurePostHogInitialized`'s `session_recording` config), single-domain
	// measurement, the same legal basis product analytics already runs on
	// outside the EEA/UK/Switzerland prior-consent zone, so there is no reason
	// for it to require a stricter gate than capture itself. Now uses the
	// identical `captureEnabled` expression. EEA/UK/Switzerland visitors are
	// unaffected: `legitimateInterestOk` only resolves `true` outside the
	// prior-consent regions (see `requiresPriorConsent` /
	// `useLegitimateInterest`), so those visitors still only reach
	// `captureEnabled` — and therefore replay — via explicit "analytics" opt-in,
	// exactly as before.
	useEffect(() => {
		if (!posthogInitialized || !posthog) return;
		try {
			if (captureEnabled) {
				posthog.startSessionRecording?.();
			} else {
				posthog.stopSessionRecording?.();
			}
		} catch {
			// posthog may not be fully ready in tests
		}
	}, [captureEnabled]);

	return (
		<PHProvider client={posthog}>
			<PostHogReadyContext.Provider value={ready}>
				<Suspense fallback={null}>
					<AttributionCapture />
					{ready ? <PostHogPageView /> : null}
					{ready ? <PostHogAuth /> : null}
				</Suspense>
				{children}
			</PostHogReadyContext.Provider>
		</PHProvider>
	);
}
